/**
 * PartialYearBanner - Enhanced UX for partial-year handling
 * 
 * Shows a visual timeline of pre-event vs post-event months,
 * with clear explanation of how data is handled during interruption.
 */

import React from 'react';
import { AlertTriangle, Calendar, TrendingDown, CheckCircle, Info } from 'lucide-react';

interface PartialYearInfo {
  coverage_year: number;
  original_months: number;
  padded_months: number;
  event_start_month: number;
  event_date?: string;
}

interface PartialYearBannerProps {
  partialYearInfo: PartialYearInfo;
  onLearnMore?: () => void;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function PartialYearBanner({ partialYearInfo, onLearnMore }: PartialYearBannerProps) {
  const { coverage_year, original_months, padded_months, event_start_month } = partialYearInfo;
  
  // Calculate which months are pre-event vs post-event
  const eventMonthIndex = event_start_month - 1; // 0-indexed
  
  // Generate timeline data
  const timelineMonths = MONTH_NAMES.map((name, idx) => {
    const isPreEvent = idx < eventMonthIndex;
    const isEventMonth = idx === eventMonthIndex;
    const isPostEvent = idx > eventMonthIndex;
    
    return {
      name,
      monthNum: idx + 1,
      isPreEvent,
      isEventMonth,
      isPostEvent,
      hasData: isPreEvent || isEventMonth,
    };
  });
  
  const preEventMonths = timelineMonths.filter(m => m.isPreEvent).length;
  const postEventMonths = timelineMonths.filter(m => m.isPostEvent).length;

  return (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-amber-100 px-4 py-3 border-b border-amber-200">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <h4 className="font-semibold text-amber-900">Mid-Year Business Interruption Detected</h4>
        </div>
      </div>
      
      <div className="p-4 space-y-4">
        {/* Timeline Visualization */}
        <div className="bg-white rounded-lg p-4 border border-amber-100">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">{coverage_year} Monthly Timeline</span>
          </div>
          
          {/* Visual Timeline */}
          <div className="flex gap-1">
            {timelineMonths.map((month, idx) => (
              <div
                key={month.name}
                className={`flex-1 relative group`}
              >
                {/* Month Bar */}
                <div
                  className={`h-8 rounded-sm flex items-center justify-center text-xs font-medium transition-all ${
                    month.isPreEvent
                      ? 'bg-green-500 text-white'
                      : month.isEventMonth
                      ? 'bg-amber-500 text-white ring-2 ring-amber-600 ring-offset-1'
                      : 'bg-gray-200 text-gray-400'
                  }`}
                >
                  {month.name}
                </div>
                
                {/* Tooltip on hover */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                  <div className={`px-2 py-1 rounded text-xs whitespace-nowrap ${
                    month.isPreEvent
                      ? 'bg-green-700 text-white'
                      : month.isEventMonth
                      ? 'bg-amber-700 text-white'
                      : 'bg-gray-700 text-white'
                  }`}>
                    {month.isPreEvent && 'Pre-event data'}
                    {month.isEventMonth && '⚡ Event started'}
                    {month.isPostEvent && 'Set to $0'}
                  </div>
                </div>
                
                {/* Event marker */}
                {month.isEventMonth && (
                  <div className="absolute -top-1 left-1/2 transform -translate-x-1/2">
                    <div className="w-3 h-3 bg-amber-600 rounded-full animate-pulse" />
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Legend */}
          <div className="flex items-center gap-4 mt-3 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-500 rounded-sm" />
              <span className="text-gray-600">Pre-event ({preEventMonths} months)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-amber-500 rounded-sm" />
              <span className="text-gray-600">Event start</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-gray-200 rounded-sm" />
              <span className="text-gray-600">Post-event ($0)</span>
            </div>
          </div>
        </div>
        
        {/* Explanation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* What We're Using */}
          <div className="bg-green-50 rounded-lg p-3 border border-green-200">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="font-medium text-green-800 text-sm">Data Used</span>
            </div>
            <ul className="text-sm text-green-700 space-y-1">
              <li>• {original_months} months of actual pre-event data</li>
              <li>• Revenue & expenses through {MONTH_NAMES[eventMonthIndex - 1] || MONTH_NAMES[0]}</li>
              <li>• Historical patterns for projection</li>
            </ul>
          </div>
          
          {/* What We're Assuming */}
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-4 h-4 text-gray-600" />
              <span className="font-medium text-gray-800 text-sm">Interruption Period</span>
            </div>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• {padded_months} months set to $0 revenue</li>
              <li>• {MONTH_NAMES[eventMonthIndex]} through Dec assumed closed</li>
              <li>• This represents business interruption loss</li>
            </ul>
          </div>
        </div>
        
        {/* Info Note */}
        <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
          <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <span className="font-medium">How this affects your claim:</span>
            <span className="text-blue-700 ml-1">
              The BI calculation compares your pre-event performance (benchmark) against the 
              interruption period. Setting post-event months to $0 captures the full loss during closure.
              {postEventMonths >= 6 && (
                <span className="block mt-1 text-amber-700 font-medium">
                  ⚠️ Note: {postEventMonths} months of zero revenue is significant. Ensure your event dates are accurate.
                </span>
              )}
            </span>
          </div>
        </div>
        
        {/* Optional Learn More */}
        {onLearnMore && (
          <button
            onClick={onLearnMore}
            className="text-sm text-amber-700 hover:text-amber-800 font-medium underline"
          >
            Learn more about partial-year calculations →
          </button>
        )}
      </div>
    </div>
  );
}
